import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, IconButton, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../config';
import { ApiManager } from '../../utils/apiManager';
import { captureAndDownloadScreenshot, sendScreenshotViaEmail } from '../../utils/screenshotCapture';
import StickyActionBar, { fullScreenHeight } from '../../components/common/StickyActionBar';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [offerAmount, setOfferAmount] = useState('25');
  // Always start empty: the device may be shared, so don't prefill a previous user's details
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [upi, setUpi] = useState('');
  const [formName, setFormName] = useState('Offer A');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const savedAmount = localStorage.getItem('current_offer_amount');
    const savedName = localStorage.getItem('current_form_name');
    if (savedAmount) setOfferAmount(savedAmount);
    if (savedName) setFormName(savedName);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!phone || !email || !upi || !name) {
      setErrorMessage('Please fill all fields');
      return;
    }

    if (phone.length !== 10) {
      setErrorMessage('Please enter a valid 10-digit phone number');
      return;
    }

    if (!['6', '7', '8', '9'].includes(phone[0])) {
      setErrorMessage('Phone number must start with 6, 7, 8, or 9');
      return;
    }

    const pendingScreenshot = localStorage.getItem('pending_screenshot');
    if (!pendingScreenshot) {
      setErrorMessage('No screenshot found. Please go back and upload a screenshot first.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert base64 to File using fetch API for perfect byte reconstruction
      const originalName = localStorage.getItem('pending_screenshot_name');
      const mime = localStorage.getItem('pending_screenshot_type') || 'image/jpeg';
      const extension = mime.split('/')[1] || 'jpg';
      const fileName = originalName || `screenshot.${extension}`;
      
      const res = await fetch(pendingScreenshot);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: mime });

      // Create FormData
      const formData = new FormData();
      formData.append('name', formName);
      formData.append('phone', phone);
      formData.append('image', file);
      formData.append('upi', upi);
      formData.append('email', email);
      formData.append('fest', 'False');

      const response = await ApiManager.fetch(`${BASE_URL}/api/form/input-ios-data/`, {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMsg = 'Submission failed';
        if (response.status === 404) {
          errorMsg = "Submission not accepted. You may have already completed this offer or it is no longer active.";
        } else if (response.status >= 500) {
          errorMsg = "System is currently busy processing other requests. Please try again in 30 seconds.";
        } else {
          try {
            const errorData = JSON.parse(responseText);
            if (errorData.non_field_errors) {
              if (errorData.non_field_errors[0].includes('unique set')) {
                errorMsg = "You have already completed this offer with this phone number!";
              } else {
                errorMsg = errorData.non_field_errors[0];
              }
            }
            else if (errorData.message) errorMsg = errorData.message;
            else if (errorData.error) errorMsg = errorData.error;
            else if (errorData.detail) errorMsg = errorData.detail;
            else errorMsg = "Verification failed. Please check your inputs.";
          } catch (e) {
            errorMsg = "Unable to verify submission. Please try again.";
          }
        }
        const error: any = new Error(errorMsg);
        error.backendResponse = responseText;
        error.status = response.status;
        throw error;
      }

      // Success
      try {
        const data = JSON.parse(responseText || '{}');
        if (data && data.id) {
          sessionStorage.setItem('submission_id', data.id);
        }
      } catch (e) {}

      // Store locally
      localStorage.setItem('phone', phone);
      localStorage.setItem('email', email);
      localStorage.setItem('upi', upi);
      localStorage.setItem('name', name);
      
      const localHistory = JSON.parse(localStorage.getItem('local_offer_history') || '[]');
      localHistory.push({
        name: formName,
        phone: phone,
        email: email,
        date: new Date().toISOString(),
        amount: offerAmount
      });
      localStorage.setItem('local_offer_history', JSON.stringify(localHistory));
      localStorage.setItem('refresh_completed_offers', 'true');
      
      // Clear pending screenshot so it doesn't take up space
      localStorage.removeItem('pending_screenshot');

      toast.success('Registration successful!');

      // Navigate to OTP verification; it picks the payment-processing variant afterwards
      setTimeout(() => {
        navigate('/verify-otp', { replace: true });
      }, 1000);

    } catch (error: any) {
      console.error(error);
      const errorMsg = error.message || 'An unexpected error occurred';
      setErrorMessage(errorMsg);

      if (error.status && error.status >= 500) {
        setTimeout(() => {
          captureAndDownloadScreenshot({
            errorMessage: errorMsg,
            errorStatus: error.status,
            userPhone: phone,
            formName: formName,
            timestamp: new Date().toISOString()
          }).then((screenshotBase64) => {
            const backendErrorMessage = error.backendResponse || errorMsg;
            return sendScreenshotViaEmail(screenshotBase64, phone, formName, upi, backendErrorMessage, error.status);
          }).catch(err => console.error(err));
        }, 300);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ ...fullScreenHeight, bgcolor: '#F2F5F1', p: 2, pb: 0, display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Top Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pt: 1 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: '#000', p: 0 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box sx={{ bgcolor: '#E6F4EA', color: '#137333', px: 2, py: 0.5, borderRadius: '20px' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '13px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              ₹{offerAmount} waiting for you
            </Typography>
          </Box>
        </Box>

        {/* Headings */}
        <Typography sx={{ color: '#0F231A', fontSize: '26px', fontWeight: 700, lineHeight: 1.1, mb: 0.5, fontFamily: '"Moan Slight", sans-serif', letterSpacing: '-1px' }}>
          Where should we send your money?
        </Typography>
        <Typography sx={{ color: '#666', fontSize: '14px', mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          We'll confirm by SMS and email.
        </Typography>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>

          {/* Error Alert */}
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage('')}
              sx={{ borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Mobile number */}
          <Box>
            <Typography sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Mobile number
            </Typography>
            <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ px: 1.75, py: 1, borderRight: '1px solid #ddd', display: 'flex', alignItems: 'center', color: '#666', fontWeight: 600, fontSize: '15px', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                +91
              </Box>
              <input
                type="tel"
                placeholder="98765 43210"
                value={phone}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                style={{ flex: 1, minWidth: 0, border: 'none', padding: '11px 14px', fontSize: '16px', outline: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              />
            </Box>
          </Box>

          {/* Email */}
          <Box>
            <Typography sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Email
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: '100%', border: 'none', padding: '11px 14px', fontSize: '16px', outline: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              />
            </Box>
          </Box>

          {/* Your name */}
          <Box>
            <Typography sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Your name
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
              <input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: '100%', border: 'none', padding: '11px 14px', fontSize: '16px', outline: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              />
            </Box>
          </Box>

          {/* UPI ID */}
          <Box>
            <Typography sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              UPI ID
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
              <input
                type="text"
                placeholder="yourname@okaxis"
                value={upi}
                onChange={(e) => setUpi(e.target.value)}
                style={{ width: '100%', border: 'none', padding: '11px 14px', fontSize: '16px', outline: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              />
            </Box>
            <Typography sx={{ color: '#666', fontSize: '12px', mt: 0.5, ml: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Find it in GPay, PhonePe or Paytm under your profile
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Submit Button */}
          <StickyActionBar>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{
                bgcolor: '#0F763F !important',
                color: '#fff',
                py: 1.6,
                borderRadius: '16px',
                fontSize: '17px',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                '&:hover': {
                  bgcolor: '#0d6535 !important',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  bgcolor: '#A5D6B6 !important',
                  color: '#fff'
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                  Submitting...
                </>
              ) : (
                'Register & get paid'
              )}
            </Button>
          </StickyActionBar>

        </Box>
      </Container>
    </Box>
  );
}
